import React, { Fragment } from "react";
import { Spinner, SpinnerSize } from "@fluentui/react";
import { Answer } from "../Answer";
import { ClarificationOptions } from "../ClarificationOptions/ClarificationOptions";
import { FollowUpSuggestions } from "../FollowUpSuggestions";
import styles from "./ChatMessageContainer.module.css";
import {
  type ToolMessageContent,
  type ChatMessage,
  type Citation,
  type ClarificationData,
  type GroundednessData,
} from "../../api";

const [ASSISTANT, TOOL, ERROR, USER] = ["assistant", "tool", "error", "user"];

export type ChatMessageContainerProps = {
  fetchingConvMessages: boolean;
  answers: ChatMessage[];
  onShowCitation: (citedDocument: Citation) => void;
  conversationId?: string;
  onClarificationSubmit?: (originalQuery: string, selectedAnswers: Record<string, string>) => void;
  onFollowUpClick?: (suggestion: string) => void;
  isGenerating?: boolean;
};

const parseCitationFromMessage = (message: ChatMessage) => {
  if (message.role === TOOL) {
    try {
      const toolMessage = JSON.parse(message.content) as ToolMessageContent;
      return toolMessage.citations;
    } catch {
      return [];
    }
  }
  return [];
};

const parseClarificationFromMessage = (message: ChatMessage): ClarificationData | null => {
  if (message.role === TOOL) {
    try {
      const toolMessage = JSON.parse(message.content) as ToolMessageContent;
      return toolMessage.clarification || null;
    } catch {
      return null;
    }
  }
  return null;
};

const parseFollowupsFromMessage = (message: ChatMessage): string[] => {
  if (message.role === TOOL) {
    try {
      const toolMessage = JSON.parse(message.content) as ToolMessageContent;
      return toolMessage.followups || [];
    } catch {
      return [];
    }
  }
  return [];
};

const parseExpandedModeFromMessage = (message: ChatMessage): boolean => {
  if (message.role === TOOL) {
    try {
      const toolMessage = JSON.parse(message.content) as ToolMessageContent;
      return toolMessage.expanded_mode || false;
    } catch {
      return false;
    }
  }
  return false;
};

const parseGroundednessFromMessage = (message: ChatMessage): GroundednessData | null => {
  if (message.role === TOOL) {
    try {
      const toolMessage = JSON.parse(message.content) as ToolMessageContent;
      return toolMessage.groundedness || null;
    } catch {
      return null;
    }
  }
  return null;
};

// Helper to find the user question that precedes an assistant answer
const findPrecedingQuestion = (answers: ChatMessage[], currentIndex: number): string => {
  for (let i = currentIndex - 1; i >= 0; i--) {
    if (answers[i].role === USER) {
      return answers[i].content;
    }
  }
  return "";
};

export const ChatMessageContainer: React.FC<ChatMessageContainerProps> = (
  props
) => {
  const {
    fetchingConvMessages,
    answers,
    onShowCitation,
    conversationId,
    onClarificationSubmit,
    onFollowUpClick,
    isGenerating,
  } = props;
  return (
    <Fragment>
      {fetchingConvMessages && (
        <div className={styles.fetchMessagesSpinner}>
          <Spinner size={SpinnerSize.medium} />
        </div>
      )}
      {!fetchingConvMessages &&
        answers.map((answer, index) => (
          <React.Fragment key={`${answer?.role}-${index}`}>
            {answer.role === USER ? (
              <div
                className={styles.chatMessageUser}
                key={`${answer?.role}-${index}`}
              >
                <div className={styles.chatMessageUserMessage}>
                  {answer.content}
                </div>
              </div>
            ) : answer.role === ASSISTANT || answer.role === ERROR ? (
              (() => {
                // Check if the preceding tool message contains clarification data
                const clarification = index > 0 ? parseClarificationFromMessage(answers[index - 1]) : null;

                if (clarification && onClarificationSubmit) {
                  // Render clarification form instead of regular answer
                  return (
                    <div
                      className={styles.chatMessageGpt}
                      key={`${answer?.role}-${index}`}
                    >
                      <ClarificationOptions
                        clarification={clarification}
                        assistantMessage={answer.content}
                        onSubmit={onClarificationSubmit}
                        disabled={isGenerating}
                      />
                    </div>
                  );
                }

                // Regular answer rendering
                // Parse follow-ups from preceding tool message
                const followups = index > 0 && answer.role === ASSISTANT
                  ? parseFollowupsFromMessage(answers[index - 1])
                  : [];
                // Only show follow-ups on the last assistant message
                const isLastAssistantMessage = index === answers.length - 1;

                return (
                  <div
                    className={styles.chatMessageGpt}
                    key={`${answer?.role}-${index}`}
                  >
                    <Answer
                      answer={{
                        answer:
                          answer.role === ASSISTANT
                            ? answer.content
                            : "Sorry, an error occurred. Try refreshing the conversation or waiting a few minutes. If the issue persists, contact your system administrator. Error: " +
                              answer.content,
                        citations:
                          answer.role === ASSISTANT
                            ? parseCitationFromMessage(answers[index - 1])
                            : [],
                      }}
                      onCitationClicked={(c) => onShowCitation(c)}
                      index={index}
                      conversationId={conversationId}
                      messageId={answer.id}
                      question={findPrecedingQuestion(answers, index)}
                      expandedMode={index > 0 && answer.role === ASSISTANT ? parseExpandedModeFromMessage(answers[index - 1]) : false}
                      groundedness={index > 0 && answer.role === ASSISTANT ? parseGroundednessFromMessage(answers[index - 1]) : null}
                    />
                    {isLastAssistantMessage && followups.length > 0 && onFollowUpClick && (
                      <FollowUpSuggestions
                        suggestions={followups}
                        onSuggestionClick={onFollowUpClick}
                        disabled={isGenerating}
                      />
                    )}
                  </div>
                );
              })()
            ) : null}
          </React.Fragment>
        ))}
    </Fragment>
  );
};
